/**
 * Microphone device changes during a call. Unplugging the headset (or a
 * USB/Bluetooth mic dropping) ends the capture track; the controller re-acquires
 * on the track's `ended`. `devicechange` covers the rest — decided here:
 *  - `recover`: the live track is dead (every mic was gone, one is back);
 *  - `restore`: the chosen mic was unplugged, the call fell back to the system
 *    one, and the chosen mic is plugged back in;
 *  - `follow-default`: «По умолчанию» is selected and the system default moved
 *    to another device (Chromium lists it as `default`, with the real device's
 *    groupId; other browsers don't, so there is nothing to follow there).
 */

export type MicSwapReason = 'recover' | 'restore' | 'follow-default';

type LiveMic = Pick<MediaStreamTrack, 'readyState' | 'getSettings'>;
type AudioInput = Pick<MediaDeviceInfo, 'deviceId' | 'groupId' | 'kind'>;

export function micSwapReason(
  track: LiveMic,
  devices: readonly AudioInput[],
  wantId: string,
): MicSwapReason | null {
  if (track.readyState === 'ended') return 'recover';
  const inputs = devices.filter((d) => d.kind === 'audioinput');
  const settings = track.getSettings();
  if (wantId && wantId !== 'default') {
    const back = settings.deviceId !== wantId && inputs.some((d) => d.deviceId === wantId);
    return back ? 'restore' : null;
  }
  const systemDefault = inputs.find((d) => d.deviceId === 'default');
  if (!systemDefault?.groupId || !settings.groupId) return null;
  return systemDefault.groupId !== settings.groupId ? 'follow-default' : null;
}

/** Devices arrive in bursts (a headset is a mic + speakers, sometimes twice). */
export const DEVICE_CHANGE_SETTLE_MS = 400;

/** `devicechange` on the current `navigator.mediaDevices`, settled into one call. */
export class DeviceChangeWatch {
  private devices: MediaDevices | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly onSettled: () => void,
    private readonly settleMs = DEVICE_CHANGE_SETTLE_MS,
  ) {}

  /** Start listening. Idempotent; follows a replaced `mediaDevices` object. */
  bind() {
    const devices = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
    if (!devices?.addEventListener || devices === this.devices) return;
    this.devices?.removeEventListener('devicechange', this.onChange);
    devices.addEventListener('devicechange', this.onChange);
    this.devices = devices;
  }

  /** Drop a burst that hasn't settled yet. */
  cancel() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private onChange = () => {
    this.cancel();
    this.timer = setTimeout(() => {
      this.timer = null;
      this.onSettled();
    }, this.settleMs);
  };
}
